import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
    Box,
    Paper,
    Stack,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Skeleton,
    Button,
    TablePagination,
    Slide,
    IconButton,
    Popover,
} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import {InfoOutlined, DeleteOutline} from '@mui/icons-material';
import {BankService, BankPage} from '@/services/manageService/bankService';
import {SnackbarField, InputField} from '@/components/common';
import {usePageCrud} from '@/hooks/usePageCrud';
import BankDetail from '@/pages/components/manage/dictionary/bank/BankDetail';

/* ======================== 删除确认 Popover ======================== */

function DeletePopover({nodeId, nodeName, onRemove}: {
    nodeId: number; nodeName: string; onRemove: (id: number) => void;
}) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const handleClick = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
    const handleClose = () => setAnchorEl(null);
    const handleConfirm = () => {
        onRemove(nodeId);
        handleClose();
    };
    return (
        <>
            <IconButton size="small" color="error" onClick={handleClick}>
                <DeleteOutline fontSize="small"/>
            </IconButton>
            <Popover open={!!anchorEl} anchorEl={anchorEl} onClose={handleClose}
                     anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}>
                <Box p={2}>
                    <Typography variant="body2" sx={{mb: 1}}>Confirm to delete "{nodeName}"?</Typography>
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button size="small" onClick={handleClose}>Cancel</Button>
                        <Button size="small" color="error" variant="contained" onClick={handleConfirm}>Confirm</Button>
                    </Stack>
                </Box>
            </Popover>
        </>
    );
}

/* ======================== 主列表组件 ======================== */

export default function BankList() {
    const theme = useTheme();
    const [data, setData] = useState<BankPage>({records: [], total: 0, size: 25, current: 1, pages: 0});
    const [loading, setLoading] = useState<boolean>(true);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false, message: '', severity: 'success'
    });
    const [keyword, setKeyword] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [detailId, setDetailId] = useState<number | undefined>(undefined);
    const {canRead, canCreate, canRemove} = usePageCrud({
        read: ['bank_list', 'bank_get'],
        create: 'admin:bank:create',
        update: 'admin:bank:update',
        remove: 'admin:bank:delete',
    });

    const initialLoadDone = useRef(false);

    const fetchData = useCallback(async (pageNum = 1, pageSize = 25, kw?: string) => {
        setLoading(true);
        try {
            const res = await BankService.getList(pageNum, pageSize, kw || undefined);
            setData(res);
        } catch (e: any) {
            setSnackbar({open: true, message: e?.message || 'Failed to load data', severity: 'error'});
        } finally {
            setLoading(false);
        }
    }, []);

    // 初始加载 + 翻页/每页条数变化
    useEffect(() => {
        fetchData(page + 1, rowsPerPage, keyword);
        initialLoadDone.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage]);

    // 防抖：搜索内容变化（跳过首次 mount）
    useEffect(() => {
        if (!initialLoadDone.current) return;
        const handler = setTimeout(() => {
            setPage(0);
            fetchData(1, rowsPerPage, keyword);
        }, 400);
        return () => clearTimeout(handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [keyword]);

    const handleRemove = async (id: number) => {
        setLoading(true);
        try {
            await BankService.deleteById(id);
            setSnackbar({open: true, message: 'Deleted successfully!', severity: 'success'});
            fetchData(page + 1, rowsPerPage, keyword);
        } catch (e: any) {
            setSnackbar({open: true, message: e?.message || 'Delete failed!', severity: 'error'});
        } finally {
            setLoading(false);
        }
    };

    return (
        <Slide direction="left" in={true} mountOnEnter unmountOnExit>
            <Box sx={{
                p: 2,
                bgcolor: 'transparent',
                height: 'calc(100vh - 120px)',
                minHeight: '70vh',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}>
                <Stack direction={{xs: 'column', sm: 'row'}} justifyContent="space-between"
                       alignItems={{xs: 'stretch', sm: 'center'}} gap={1} mb={2} flexShrink={0}>
                    <Typography variant="h6" fontWeight={700} color="text.primary">Bank Dictionary</Typography>
                    <Stack direction={{xs: 'column', sm: 'row'}} spacing={1}
                           alignItems={{xs: 'stretch', sm: 'center'}}>
                        <InputField size="small" label="Search" value={keyword}
                                    onChange={e => setKeyword(e.target.value)}/>
                        {canCreate && (
                        <Button variant="contained" color="secondary"
                                sx={{textTransform: 'none', fontWeight: 600, borderRadius: theme.shape.borderRadius}}
                                onClick={() => {
                                    setDetailId(undefined);
                                    setDialogOpen(true);
                                }}>Create</Button>
                        )}
                    </Stack>
                </Stack>

                <TableContainer
                    component={Paper}
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        overflow: 'auto',
                        boxShadow: theme.shadows[2],
                        bgcolor: 'background.paper',
                    }}
                >
                    <Table
                        size="small"
                        stickyHeader
                        sx={{
                            minWidth: 700,
                            '& .MuiTableCell-stickyHeader': {
                                bgcolor: 'background.paper',
                                zIndex: 3,
                                borderBottom: `1px solid ${theme.palette.divider}`,
                            },
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{fontWeight: 700}}>Bank Code</TableCell>
                                <TableCell sx={{fontWeight: 700}}>HK Code</TableCell>
                                <TableCell sx={{fontWeight: 700}}>Name (CN)</TableCell>
                                <TableCell sx={{fontWeight: 700}}>Name (EN)</TableCell>
                                <TableCell sx={{fontWeight: 700}}>Account Pattern</TableCell>
                                <TableCell sx={{fontWeight: 700, width: 120}}>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading && [...Array(rowsPerPage).keys()].map(i => (
                                <TableRow key={i}>
                                    {[...Array(6).keys()].map(j => (
                                        <TableCell key={j}><Skeleton variant="text"/></TableCell>
                                    ))}
                                </TableRow>
                            ))}
                            {!loading && data.records.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">No data</TableCell>
                                </TableRow>
                            )}
                            {!loading && data.records.map((r, idx) => (
                                <TableRow hover key={r.id ?? `row-${idx}`}>
                                    <TableCell>{r.bankCode || '-'}</TableCell>
                                    <TableCell>{r.hkBankCode || '-'}</TableCell>
                                    <TableCell>{r.nameCn || '-'}</TableCell>
                                    <TableCell>{r.nameEn || '-'}</TableCell>
                                    <TableCell>{r.accountPattern || '-'}</TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={0.5}>
                                            {canRead && (
                                            <IconButton size="small" color="primary" onClick={() => {
                                                setDetailId(r.id);
                                                setDialogOpen(true);
                                            }}>
                                                <InfoOutlined fontSize="small"/>
                                            </IconButton>
                                            )}
                                            {canRemove && r.id != null && (
                                                <DeletePopover nodeId={r.id}
                                                               nodeName={r.bankCode || r.nameEn || ''}
                                                               onRemove={handleRemove}/>
                                            )}
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={data.total}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={e => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    sx={{flexShrink: 0}}
                />

                <BankDetail
                    open={dialogOpen}
                    bankId={detailId}
                    onClose={(p?: { refreshed?: boolean; message?: string; severity?: 'success' | 'error' }) => {
                        setDialogOpen(false);
                        setDetailId(undefined);
                        if (p?.message) setSnackbar({
                            open: true,
                            message: p.message,
                            severity: p.severity || 'success'
                        });
                        if (p?.refreshed) {
                            fetchData(page + 1, rowsPerPage, keyword);
                        }
                    }}
                />
                <SnackbarField
                    open={snackbar.open}
                    message={snackbar.message}
                    severity={snackbar.severity}
                    onClose={() => setSnackbar({...snackbar, open: false})}
                />
            </Box>
        </Slide>
    );
}
